import './Table.css';
import { Link } from 'react-router-dom';
import BootstrapTable, { BootstrapTableProps, SelectRowProps } from 'react-bootstrap-table-next';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import paginationFactory from 'react-bootstrap-table2-paginator';
import filterFactory, { selectFilter } from 'react-bootstrap-table2-filter';
import { documentConfig, DocumentStatusType } from '../../config';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  setCurrentPage,
  setTablePageSize,
  setFilteredDocuments,
  deleteFilteredDocuments,
  deleteDocuments,
  publishedDocument,
} from '../../redux/actions';
import { ReactNode, useEffect, useState } from 'react';
import CustomSearch from '../ui/CustomSearch/CustomSearch';
import { Button } from 'react-bootstrap';
import ConfirmationModal from './ConfirmationModal';
import { DocumentType, OrderType, UserInfoType } from '../../types/AppTypes';
import { deleteEpisodes, publishDocument } from '../../utils/api';
import { getDurationFormatedValue, formatOptions } from '../../utils/utils';
import { RootState } from '../../redux/store';
import { useSelector } from 'react-redux';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sortData } from '../../utils/arrayUtils';

type Props = {
  documents: DocumentType[];
};

interface DocumentCustomers {
  item: string;
}

const adminUsernames = process.env?.REACT_APP_ADMIN_USERNAMES;

const Table = ({ documents }: Props): JSX.Element => {
  const [documentCustomers, setDocumentCustomers] = useState({});
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | number>(0);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [action, setAction] = useState<string>('');
  const [skipDocumentsReload, setSkipDocumentsReload] = useState(false);

  const searchQuery =
    localStorage.getItem('documents_search_input') !== null && localStorage.getItem('documents_search_input') !== ''
      ? localStorage.getItem('documents_search_input')
      : '';
  const data = documents && documents.length ? documents : [];

  const userInfo = useSelector((state: RootState) => state.userInfo.userInfo) as UserInfoType;

  const { tablePageSize, currentPage, user, filteredDocuments } = useAppSelector((state) => state.documents);

  const dispatch = useAppDispatch();

  function documentFormatter(cell, row) {
    return <div style={{ wordWrap: 'break-word' }}>{cell}</div>;
  }

  const permissions = userInfo?.permissions;

  const sortProps = {
    sort: true,
    onSort: (field: string, order: OrderType) => {
      dispatch(setFilteredDocuments(sortData(filteredDocuments, field, order)));
    },
  };

  const columns = [
    {
      dataField: 'customerId',
      text: 'Customer ID',
      headerAlign: 'center',
      align: 'center',
      sort: true,
      formatter: (cell: string): string => {
        return documentCustomers[cell] || cell;
      },
      filter: selectFilter({
        options: documentCustomers,
        onFilter: (filterValue: any) => {
          setSelectedCustomerId(filterValue.toString());
        },
        placeholder: 'Select All',
        defaultValue: localStorage.getItem('selectedCustomerId') !== null ? localStorage.getItem('selectedCustomerId') : '',
      }),
    },
    {
      dataField: 'documentId',
      text: 'Document ID',
      headerAlign: 'center',
      align: 'center',
      ...sortProps,
      formatter: documentFormatter,
    },
    {
      dataField: 'version',
      text: 'Version',
      formatter: (cell: number): number => {
        // set version if not provided
        return cell || documentConfig.defaultVersion;
      },
      headerAlign: 'center',
      align: 'center',
      ...sortProps,
      headerStyle: (column, colIndex) => {
        const mediaQueryMedium = window.matchMedia('(max-width: 767px)');
        const mediaQuerySmall = window.matchMedia('(max-width: 575px)');
        if (mediaQuerySmall.matches) {
          return { width: '50px', textAlign: 'center' };
        } else if (mediaQueryMedium.matches) {
          return { width: '65px', textAlign: 'center' };
        }
        return { width: '80px', textAlign: 'center' };
      },
    },
    {
      dataField: 'duration',
      text: 'Duration',
      headerAlign: 'center',
      align: 'center',
      ...sortProps,
      formatter: (cell: number): string => {
        return getDurationFormatedValue(cell);
      },
    },
    {
      dataField: 'state',
      text: 'Status',
      formatter: (cell: string): any => {
        const status = documentConfig.status[cell as keyof DocumentStatusType];
        return status || '';
      },
      headerAlign: 'center',
      align: 'center',
      ...sortProps,
    },
    {
      dataField: 'text',
      text: 'Text',
      hidden: true,
    },
    {
      dataField: 'created',
      text: 'Created',
      formatter: (cell: string): string => {
        const utcDate = new Date(cell);

        const date = utcDate.toLocaleTimeString('en-US', formatOptions);

        return date;
      },
      headerAlign: 'center',
      align: 'center',
      ...sortProps,
    },
    {
      dataField: 'audioCreated',
      text: 'Last updated',
      formatter: (cell: string): string => {
        const utcDate = new Date(cell);

        const date = utcDate.toLocaleTimeString('en-US', formatOptions);

        return date;
      },
      headerAlign: 'center',
      align: 'center',
      ...sortProps,
    },
    {
      dataField: 'username',
      text: 'Updated By',
      headerAlign: 'center',
      align: 'center',
      ...sortProps,
      hidden: adminUsernames?.includes(user?.name.toLowerCase()) || userInfo?.isAdmin ? false : true,
    },
    {
      dataField: 'pdfUrl',
      text: 'PDF',
      headerAlign: 'center',
      align: 'center',
      ...sortProps,
      formatter: (_cell: any, row: any) => {
        if (_cell != '') {
          return (
            <a href={`${_cell}`} target="_blank" rel="noreferrer">
              <FontAwesomeIcon icon={faFilePdf} color="#007BFF" />
            </a>
          );
        }
        return _cell;
      },
      headerStyle: (column, colIndex) => {
        const mediaQueryMedium = window.matchMedia('(max-width: 767px)');
        const mediaQuerySmall = window.matchMedia('(max-width: 575px)');
        if (mediaQuerySmall.matches) {
          return { width: '50px', textAlign: 'center' };
        } else if (mediaQueryMedium.matches) {
          return { width: '65px', textAlign: 'center' };
        }
        return { width: '80px', textAlign: 'center' };
      },
    },
    {
      dataField: 'actions',
      text: 'Actions',
      isDummyField: true,
      csvExport: false,
      hidden: permissions?.includes('documents.edit') || userInfo?.isAdmin ? false : true,
      formatter: (_cell: any, row: any) => <Link to={`/document/${row.documentId}`}>Edit</Link>,
      headerAlign: 'center',
      align: 'center',
      headerStyle: (column, colIndex) => {
        const mediaQueryMedium = window.matchMedia('(max-width: 767px)');
        const mediaQuerySmall = window.matchMedia('(max-width: 575px)');
        if (mediaQuerySmall.matches) {
          return { width: '50px', textAlign: 'center' };
        } else if (mediaQueryMedium.matches) {
          return { width: '65px', textAlign: 'center' };
        }
        return { width: '80px', textAlign: 'center' };
      },
    },
  ];

  console.log('userName', user, 'userInfo', userInfo, 'useradmins', adminUsernames);

  const selectDocumentsRow: SelectRowProps<any> = {
    mode: 'checkbox',
    bgColor: '#ddeeff',
    onSelect: (row, isSelected, rowIndex, e) => {
      const rowDocumentId: string = row['documentId'];
      const status = documentConfig.status[row.state as keyof DocumentStatusType];
      if (isSelected) {
        setSelectedRows((curr) => [...curr, rowDocumentId]);
        if (status === 'Ready') {
          setSelectedDocs((curr) => [...curr, rowDocumentId]);
        }
      } else {
        const selectedArray = [...selectedRows];
        const filteredArray = selectedArray.filter((docId) => docId !== rowDocumentId);
        setSelectedRows(filteredArray);
        if (status === 'Ready') {
          setSelectedDocs((selectedDocs) => {
            const newItems = selectedDocs.filter((docId) => docId !== row.documentId);
            return newItems;
          });
        }
      }
    },
    onSelectAll(isSelect, rows, e) {
      const selectedAllRows: string[] = [];
      const selectedAllDocs: string[] = [];
      let status: string = '';
      if (isSelect) {
        rows.map((rowItem) => {
          selectedAllRows.push(rowItem.documentId);
          status = documentConfig.status[rowItem.state as keyof DocumentStatusType];
          if (status === 'Ready') {
            selectedAllDocs.push(rowItem.documentId);
          }
        });
        setSelectedRows(selectedAllRows);
        setSelectedDocs(selectedAllDocs);
      } else {
        setSelectedRows([]);
        setSelectedDocs([]);
      }
    },
  };

  const pagination = paginationFactory({
    sizePerPage: parseInt(localStorage.getItem('documents-table-size') || '', 10) || tablePageSize,
    page: currentPage,
    onPageChange: (page) => {
      dispatch(setCurrentPage(page));
    },
    onSizePerPageChange: (sizePage) => {
      sizePerPageDocumentsChange(sizePage);
      dispatch(setTablePageSize(sizePage));
    },
  });

  const sizePerPageDocumentsChange = (sizePerPage: number) => {
    localStorage.setItem('documents-table-size', sizePerPage.toString());
  };

  const onConfirmationAction = async () => {
    if (action === 'delete') {
      deleteEpisodes(selectedRows)
        .then((res) => {
          setSkipDocumentsReload(true);
          dispatch(deleteFilteredDocuments(selectedRows));
          dispatch(deleteDocuments(selectedRows));
          setSelectedRows([]);
        })
        .catch((error: any) => {
          console.log('errorOnDeleteEpisodes', error.message);
        })
        .finally(() => {
          setShowModal(false);
        });
    } else if (action === 'publish') {
      if (selectedDocs.length === 0) {
        setShowModal(false);
      } else {
        selectedDocs.forEach((docId) => {
          if (documents.some((doc) => doc.documentId === docId && doc.state !== 'PUBLISH')) {
            publishDocument(docId)
              .then((response) => {
                dispatch(publishedDocument(docId));
                setSelectedDocs([]);
              })
              .catch((error) => {
                console.log('errorOnPublishEpisode', error.message);
              })
              .finally(() => {
                setShowModal(false);
              });
          }
        });
      }
    }
  };

  const onModalToggle = (opt: 'delete' | 'publish' | '') => {
    setAction(opt);
    setShowModal((curr) => !curr);
  };

  useEffect(() => {
    const documentsCustomerSet = new Set<any>();

    let documentCustomerObject = {};
    const editableDocumentsArray = [...documents];
    editableDocumentsArray?.sort((a, b) => {
      const alpha = a!.customerId!.toUpperCase();
      const beta = b!.customerId!.toUpperCase();

      return alpha < beta ? -1 : alpha > beta ? 1 : 0;
    });

    editableDocumentsArray?.forEach((document) => documentsCustomerSet.add(document['customerId']));

    documentsCustomerSet.forEach((value) => (documentCustomerObject[value] = value));
    setDocumentCustomers({ ...documentCustomerObject });

    if (!skipDocumentsReload) {
      dispatch(setFilteredDocuments(documents));
    } else {
      setSkipDocumentsReload(true);
    }
  }, [documents]);

  useEffect(() => {
    if (selectedCustomerId.toString() !== '0') {
      localStorage.setItem('selectedCustomerId', selectedCustomerId.toString());
    }
  }, [selectedCustomerId]);

  return (
    <div style={{ marginBottom: '15px' }}>
      <ToolkitProvider
        keyField="documentId"
        data={data}
        columns={columns}
        striped
        hover
        condensed
        sort={{ dataField: 'created', order: 'desc' }}
        search={{
          defaultSearch: '',
          afterSearch: (newResult) => {
            dispatch(setFilteredDocuments(newResult));
          },
        }}
      >
        {(props: {
          searchProps: {
            searchText: string | number;
            onSearch: (searchText: string | number) => void;
            onClear: () => void;
          };
          baseProps: JSX.IntrinsicAttributes &
            JSX.IntrinsicClassAttributes<BootstrapTable<any, number>> &
            Readonly<BootstrapTableProps<any, number>> &
            Readonly<{ children?: ReactNode }>;
          documentCustomers: DocumentCustomers;
        }) => (
          <div>
            {/* <SearchBar {...props.searchProps} /> */}
            <div className="d-flex justify-content-between">
              <CustomSearch
                {...props.searchProps}
                defaultValue={searchQuery}
                localStorageItemName="documents_search_input"
              />

              {(permissions?.includes('documents.edit') || userInfo?.isAdmin) &&
                selectedRows.length > 0 &&
                selectedDocs.length > 0 && (
                  <div style={{ marginLeft: 'auto' }}>
                    <Button variant="primary" onClick={() => onModalToggle('publish')}>
                      Publish
                    </Button>
                  </div>
                )}
              {(permissions?.includes('documents.delete') || userInfo?.isAdmin) && selectedRows.length > 0 && (
                <div style={{ marginLeft: '8px' }}>
                  <Button variant="danger" onClick={() => onModalToggle('delete')}>
                    Delete
                  </Button>
                </div>
              )}
            </div>
            <hr />
            {Object.entries(documentCustomers).length > 0 && (
              <BootstrapTable
                {...props.baseProps}
                pagination={pagination}
                headerWrapperClasses={'break-word'}
                rowStyle={{ wordBreak: 'break-word' }}
                wrapperClasses={'break-word'}
                filter={filterFactory()}
                selectRow={
                  adminUsernames?.includes(user?.name.toLowerCase()) ||
                  permissions?.includes('documents.delete') ||
                  userInfo?.isAdmin
                    ? selectDocumentsRow
                    : undefined
                }
              />
            )}
          </div>
        )}
      </ToolkitProvider>
      <ConfirmationModal
        show={showModal}
        discardAction={() => onModalToggle('')}
        confirmAction={onConfirmationAction}
        action={action}
      />
    </div>
  );
};

export default Table;
